/**
 * NotAuthorized Component
 * 
 * Displays when user is authenticated but lacks required capability (403).
 * Shows Access Denied message with required capability and escape hatches.
 */

import { useNavigate } from 'react-router-dom';
import { useCapabilities } from './CapabilityProvider';
import { clearCapabilityCache } from '../../services/capabilityService';
import { useState } from 'react';

export default function NotAuthorized({ requiredCapability }) {
  const navigate = useNavigate();
  const { has, me, refreshCapabilities, error } = useCapabilities();
  const [refreshing, setRefreshing] = useState(false);
  
  const urlParams = new URLSearchParams(window.location.search);
  const isDebug = urlParams.get('debug') === '1' || window.location.hostname.includes('.dev.') || window.location.hostname.includes('localhost');
  
  // Find first accessible pinned route
  const pinnedRoutes = [
    { path: '/admin/notifications', requires: null }, // Uses OR logic in page
    { path: '/admin/ops', requires: null }, // Uses OR logic in page
  ];
  
  const findSafeRoute = () => {
    // Check if user has any of the common capabilities
    const hasAnyAccess = has('admin:integrations:read') || 
                        has('admin:audit:read') || 
                        has('admin:owner-dashboard:read');
    
    if (hasAnyAccess) {
      // Return first pinned route (they should have access via OR logic)
      return pinnedRoutes[0]?.path || '/admin';
    }
    return null;
  };
  
  const handleReAuthenticate = async () => {
    setRefreshing(true);
    try {
      // Force refresh capabilities from backend
      await refreshCapabilities();
      // After refresh, try navigating to a safe page
      const safeRoute = findSafeRoute();
      if (safeRoute) {
        navigate(safeRoute);
      } else {
        // Still no access, show message
        alert('Still no access after refresh. Please sign out and log in again.');
      }
    } catch (err) {
      console.error('Failed to refresh capabilities:', err);
      alert('Failed to refresh capabilities. Please try signing out and logging in again.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSignOut = async () => {
    // Clear capability cache
    clearCapabilityCache();
    
    // Clear any auth storage (cookies are httpOnly, so we can't clear them client-side)
    // In dev mode, clearing cache is sufficient
    // In production, would need logout endpoint
    
    // Reload to clear state
    window.location.href = '/';
  };
  
  const handleSwitchAccount = async () => {
    await handleSignOut();
    // After reload, user can log in with different account
  };
  
  const handleGoToSafePage = () => {
    const safeRoute = findSafeRoute();
    if (safeRoute) {
      navigate(safeRoute);
    } else {
      // No safe route available, show sign out option
      handleSignOut();
    }
  };
  
  const safeRoute = findSafeRoute();
  
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Access Denied</h1>
        <p style={styles.message}>
          You don't have access to this page or action.
        </p>
        {requiredCapability && (
          <div style={styles.details}>
            <p style={styles.detailLabel}>Required capability:</p>
            <code style={styles.capability}>{requiredCapability}</code>
          </div>
        )}
        
        <div style={styles.actions}>
          {/* If user is super admin but has no capabilities, show prominent re-authenticate */}
          {me?.isSuperAdmin && (!me?.capabilities || me.capabilities.length === 0) && (
            <button
              onClick={handleReAuthenticate}
              style={styles.button}
              disabled={refreshing}
              aria-label="Refresh permissions"
            >
              {refreshing ? 'Refreshing...' : 'Re-authenticate / Refresh Permissions'}
            </button>
          )}
          {/* Always show Refresh Permissions prominently */}
          <button
            onClick={handleReAuthenticate}
            style={styles.button}
            disabled={refreshing}
            aria-label="Refresh permissions"
          >
            {refreshing ? 'Refreshing...' : 'Refresh Permissions'}
          </button>
          {safeRoute && (
            <button
              onClick={handleGoToSafePage}
              style={styles.buttonSecondary}
              aria-label="Go to a page you have access to"
            >
              Go to Safe Page
            </button>
          )}
          <button
            onClick={handleSignOut}
            style={styles.buttonSecondary}
            aria-label="Sign out"
          >
            Sign Out
          </button>
          <button
            onClick={handleSwitchAccount}
            style={styles.buttonSecondary}
            aria-label="Switch account"
          >
            Switch Account
          </button>
          <button
            onClick={() => {
              clearCapabilityCache();
              window.location.reload();
            }}
            style={styles.buttonSecondary}
            aria-label="Clear session and reload"
          >
            Clear Session & Reload
          </button>
        </div>
        
        <p style={styles.help}>
          If you believe you should have access, please contact your administrator or request access from your team lead.
        </p>
        
        {/* Debug panel (dev only) */}
        {isDebug && (
          <div style={styles.debugPanel}>
            <h3 style={styles.debugTitle}>Debug Information</h3>
            <div style={styles.debugContent}>
              <div><strong>Status:</strong> Unauthorized (403)</div>
              <div><strong>Last /admin/me Status:</strong> {error?.status || (me ? '200' : 'unknown')}</div>
              <div><strong>User ID:</strong> {me?.actorId || me?.id || '(not loaded)'}</div>
              <div><strong>Is Super Admin:</strong> {me?.isSuperAdmin ? 'true' : 'false'}</div>
              <div><strong>Super Admin Bypass Engaged:</strong> {me?.isSuperAdmin ? 'NO (should be YES - this is a bug!)' : 'N/A'}</div>
              <div><strong>Capability Count:</strong> {me?.capabilities?.length || me?.capabilityCount || 0}</div>
              <div><strong>Required Capability:</strong> {requiredCapability || '(none specified)'}</div>
              <div><strong>Has Required:</strong> {requiredCapability ? (has(requiredCapability) ? 'true' : 'false') : 'N/A'}</div>
              {me?.buildStamp && (
                <div><strong>Build Stamp:</strong> {me.buildStamp}</div>
              )}
              {me?.isSuperAdmin && (
                <div style={styles.debugWarning}>
                  ⚠️ Super admin should never see Access Denied. If you see this, the super admin bypass is not working.
                </div>
              )}
              {me?.capabilities && me.capabilities.length > 0 && (
                <div style={styles.debugCaps}>
                  <strong>Capabilities (first 10):</strong>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', fontSize: '0.75rem' }}>
                    {me.capabilities.slice(0, 10).map(cap => (
                      <li key={cap}>{cap}</li>
                    ))}
                    {me.capabilities.length > 10 && <li>... and {me.capabilities.length - 10} more</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: '2rem',
  },
  content: {
    maxWidth: '600px',
    textAlign: 'center',
    backgroundColor: '#fff',
    padding: '3rem',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '1rem',
  },
  message: {
    fontSize: '1.125rem',
    color: '#374151',
    marginBottom: '1.5rem',
  },
  details: {
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f3f4f6',
    borderRadius: '0.375rem',
  },
  detailLabel: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginBottom: '0.5rem',
  },
  capability: {
    display: 'block',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
    color: '#111827',
    backgroundColor: '#fff',
    padding: '0.5rem',
    borderRadius: '0.25rem',
    border: '1px solid #d1d5db',
  },
  help: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginTop: '1.5rem',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    marginTop: '2rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  button: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#fff',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonSecondary: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#374151',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  debugPanel: {
    marginTop: '2rem',
    padding: '1rem',
    backgroundColor: '#f3f4f6',
    borderRadius: '0.375rem',
    border: '1px solid #d1d5db',
    fontSize: '0.875rem',
  },
  debugTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '0.75rem',
    color: '#111827',
  },
  debugContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    color: '#374151',
  },
  debugCaps: {
    marginTop: '0.5rem',
  },
  debugWarning: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#fee2e2',
    borderRadius: '0.25rem',
    color: '#991b1b',
    fontWeight: 'bold',
  },
};
